import type {
  SafetyPortfolioAttentionItem,
  SafetyPortfolioSiteCoverage,
} from '../../services/reportsApi';
import type {
  AttentionMetricKey,
  WorkspaceAttentionItem,
  WorkspaceAttentionMetric,
} from './workspaceAttention';
import { ATTENTION_METRIC_LABELS } from './safetyCasesWorkspacePresentationCopy';
import {
  INVESTIGATE_LABEL,
  OPEN_SAFETY_CASE_LABEL,
  REVIEW_SAFETY_SCAN_LABEL,
} from './managedSafetyBuyerUx';
import {
  buildSiteOwnershipIndex,
  formatAttentionLocation,
  resolveSiteOwnershipMetadata,
} from './workspacePortfolioOwnership';

function normalize(value?: string | null): string {
  return String(value || '').trim().toLowerCase();
}

function portfolioMetricKey(item: SafetyPortfolioAttentionItem): AttentionMetricKey {
  const type = normalize(item.item_type);
  const workState = normalize(item.work_state || item.status_label);
  if (type.includes('risk pattern')) return 'needs_review';
  if (type.includes('activity') || workState.includes('new activity')) return 'new_activity';
  if (workState.includes('sign-off') || workState.includes('signoff')) return 'awaiting_signoff';
  if (workState.includes('coverage') || workState.includes('failed')) return 'coverage_run';
  return 'active_cases';
}

function portfolioAction(key: AttentionMetricKey): { label: string; kind: WorkspaceAttentionItem['actionKind'] } {
  if (key === 'needs_review') return { label: INVESTIGATE_LABEL, kind: 'review_pattern' };
  if (key === 'new_activity') return { label: OPEN_SAFETY_CASE_LABEL, kind: 'open_case' };
  if (key === 'awaiting_signoff') return { label: REVIEW_SAFETY_SCAN_LABEL, kind: 'sign_scan' };
  if (key === 'coverage_run') return { label: 'Resolve coverage issue', kind: 'resolve_coverage' };
  return { label: OPEN_SAFETY_CASE_LABEL, kind: 'open_case' };
}

function portfolioAttentionTitle(item: SafetyPortfolioAttentionItem): string {
  const type = String(item.item_type || '').trim();
  const workflow = String(item.workflow_id || item.item_id || '').trim();
  if (type && workflow) return `${type} ${workflow}`;
  return type || workflow || 'Portfolio item';
}

function portfolioAttentionEvidence(item: SafetyPortfolioAttentionItem): string {
  const coverage = item.coverage_context || {};
  const parts = [
    coverage.scan_workflow_state,
    coverage.scan_result,
    coverage.impact_measurement_status,
    coverage.measurement_trust_level,
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  return parts.length > 0 ? parts.join(' | ') : 'Coverage context returned by site';
}

export function buildPortfolioAttentionItems(
  items: SafetyPortfolioAttentionItem[] | undefined,
  siteCoverage?: SafetyPortfolioSiteCoverage[],
): WorkspaceAttentionItem[] {
  const ownershipBySite = buildSiteOwnershipIndex(siteCoverage);
  return (items || []).map((item, index) => {
    const siteId = String(item.site?.site_id || '').trim();
    const siteLabel = String(item.site?.display_name || siteId || 'Authorized site').trim();
    const siteOwnership = resolveSiteOwnershipMetadata({
      ...item.site,
      ...(ownershipBySite[siteId] || {}),
      region_label: item.site?.region_label || ownershipBySite[siteId]?.regionLabel || null,
      site_owner: item.site?.site_owner || ownershipBySite[siteId]?.siteOwner || null,
    });
    const workflowId = String(item.workflow_id || item.item_id || `portfolio-item-${index}`).trim();
    const metricKey = portfolioMetricKey(item);
    const action = portfolioAction(metricKey);
    const caseId = String((item as { finding_id?: string | null; safety_case_id?: string | null }).finding_id
      || (item as { safety_case_id?: string | null }).safety_case_id
      || '').trim() || null;
    const reportId = String((item as { report_id?: string | null }).report_id || '').trim() || null;
    return {
      id: `portfolio:${siteId || 'site'}:${workflowId}`,
      metricKey,
      itemType: metricKey === 'needs_review'
        ? 'risk_pattern_review'
        : metricKey === 'new_activity'
          ? 'new_activity'
          : metricKey === 'awaiting_signoff'
            ? 'awaiting_signoff'
            : metricKey === 'coverage_run'
              ? 'coverage_run'
              : 'active_safety_case',
      priority: metricKey === 'new_activity' || metricKey === 'awaiting_signoff' ? 'high' : 'medium',
      priorityRank: metricKey === 'new_activity' ? 4 : metricKey === 'awaiting_signoff' ? 1 : metricKey === 'needs_review' ? 2 : 5,
      itemLabel: portfolioAttentionTitle(item),
      evidence: portfolioAttentionEvidence(item),
      location: formatAttentionLocation(siteLabel, siteOwnership.regionLabel),
      workState: String(item.work_state || item.status_label || 'Attention').trim() || 'Attention',
      owner: String(item.owner || '').trim() || 'Unassigned',
      ownerIdentity: String(item.owner_identity || '').trim() || null,
      updatedAt: item.updated_at || null,
      dueAt: null,
      isOverdue: false,
      isUnowned: !String(item.owner || '').trim(),
      actionLabel: action.label,
      actionKind: (caseId || reportId) ? action.kind : 'open_site_workspace',
      sourceKey: workflowId,
      workflowId,
      reportId,
      safetyCaseId: caseId,
      siteId,
      regionLabel: siteOwnership.regionLabel,
      siteOwner: siteOwnership.siteOwner,
    };
  });
}

export function buildPortfolioAttentionMetrics(items: WorkspaceAttentionItem[]): WorkspaceAttentionMetric[] {
  const countFor = (key: AttentionMetricKey) => items.filter((item) => item.metricKey === key).length;
  const labels: Array<[AttentionMetricKey, string, string]> = [
    ['needs_review', ATTENTION_METRIC_LABELS.needs_review, 'Across authorized sites'],
    ['active_cases', ATTENTION_METRIC_LABELS.active_cases, 'Loaded with site context'],
    ['awaiting_signoff', ATTENTION_METRIC_LABELS.awaiting_signoff, 'Decision required'],
    ['new_activity', ATTENTION_METRIC_LABELS.new_activity, 'After latest signed Scan'],
    ['coverage_run', ATTENTION_METRIC_LABELS.coverage_run, 'Coverage context required'],
    ['overdue', ATTENTION_METRIC_LABELS.overdue, 'Not aggregated in portfolio contract'],
    ['unowned', ATTENTION_METRIC_LABELS.unowned, 'Owner assignment needed'],
  ];
  return labels.map(([key, label, secondary]) => ({
    key,
    label,
    count: key === 'unowned'
      ? items.filter((item) => item.isUnowned).length
      : key === 'overdue'
        ? items.filter((item) => item.isOverdue).length
        : countFor(key),
    secondary,
  }));
}

export function formatSiteCoverageSummary(siteCoverage: SafetyPortfolioSiteCoverage[] | undefined): string {
  const sites = siteCoverage || [];
  const loaded = sites.filter((site) => site.included && site.data_state === 'loaded').length;
  const denied = sites.filter((site) => site.data_state === 'permission_denied').length;
  const unavailable = sites.filter((site) => site.data_state === 'unavailable').length;
  return `${loaded} site${loaded === 1 ? '' : 's'} loaded | ${denied} denied | ${unavailable} unavailable`;
}
